// Visit tracking utility for storefront
class VisitTracker {
  constructor() {
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionId = this.getOrCreateSessionId();
    this.startTime = Date.now();
    this.isTracking = false;
    this.merchantId = null;
    this.storeId = null;
  }

  // Generate or retrieve visitor ID
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('revstar_visitor_id');
    if (!visitorId) {
      visitorId = 'visitor_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('revstar_visitor_id', visitorId);
    }
    return visitorId;
  }

  // Generate or retrieve session ID
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('revstar_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      sessionStorage.setItem('revstar_session_id', sessionId);
    }
    return sessionId;
  }

  // Get device information
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let device = 'desktop';
    let browser = 'Unknown';
    let os = 'Unknown';

    // Detect device
    if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      device = 'mobile';
    } else if (/Tablet|iPad/i.test(userAgent)) {
      device = 'tablet';
    }

    // Detect browser
    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
    } else if (userAgent.includes('Safari')) {
      browser = 'Safari';
    } else if (userAgent.includes('Edge')) {
      browser = 'Edge';
    }

    // Detect OS
    if (userAgent.includes('Windows')) {
      os = 'Windows';
    } else if (userAgent.includes('Mac')) {
      os = 'macOS';
    } else if (userAgent.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iOS')) {
      os = 'iOS';
    }

    return { device, browser, os };
  }

  // Get UTM parameters
  getUTMParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utmSource: urlParams.get('utm_source') || '',
      utmMedium: urlParams.get('utm_medium') || '',
      utmCampaign: urlParams.get('utm_campaign') || '',
      utmTerm: urlParams.get('utm_term') || '',
      utmContent: urlParams.get('utm_content') || ''
    };
  }

  // Get QR tracking parameters
  getQRParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const qrType = urlParams.get('qr_type');
    const qrId = urlParams.get('qr_id');
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');

    // Detect if this is a QR code scan
    const isQRScan = utmSource === 'qr' || utmMedium === 'qr_code' || qrType;

    return {
      isQRScan,
      qrCodeType: qrType || null, // STORE, PRODUCT, CHECKOUT
      qrId: qrId || null,
      productId: qrType === 'PRODUCT' ? qrId : null
    };
  }

  // Get deep link attribution token from URL or localStorage
  getDeepLinkToken() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('dl_token');

    if (token) {
      // Store in localStorage for persistence across the session
      localStorage.setItem('dl_token', token);

      // Clean the URL (remove dl_token param without page reload)
      urlParams.delete('dl_token');
      const cleanUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`
        : `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, '', cleanUrl);

      return token;
    }

    // Check localStorage for previously stored token
    const stored = localStorage.getItem('dl_token');
    if (stored) {
      // Check if token is still valid (30 day TTL)
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        const expiry = payload.exp * 1000;
        if (Date.now() > expiry) {
          localStorage.removeItem('dl_token');
          return null;
        }
      } catch {
        // If we can't decode, keep it — backend will verify
      }
      return stored;
    }

    return null;
  }

  // Clear deep link token after successful conversion
  static clearDeepLinkToken() {
    localStorage.removeItem('dl_token');
  }

  // Determine source type from referrer and UTM parameters
  getSourceType() {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const referrer = document.referrer;

    // QR code source
    if (utmSource === 'qr' || utmMedium === 'qr_code') {
      return 'QR';
    }

    // Email source
    if (utmSource === 'email' || utmMedium === 'email') {
      return 'EMAIL';
    }

    // Social media sources
    if (utmSource === 'facebook' || utmSource === 'instagram' || utmSource === 'twitter' ||
        utmSource === 'linkedin' || utmSource === 'tiktok' || utmMedium === 'social') {
      return 'SOCIAL';
    }

    // Check referrer for social platforms
    if (referrer) {
      const lowerReferrer = referrer.toLowerCase();
      if (lowerReferrer.includes('facebook.com') || lowerReferrer.includes('instagram.com') ||
          lowerReferrer.includes('twitter.com') || lowerReferrer.includes('x.com') ||
          lowerReferrer.includes('linkedin.com') || lowerReferrer.includes('tiktok.com')) {
        return 'SOCIAL';
      }
      if (lowerReferrer.includes('google.com') || lowerReferrer.includes('bing.com')) {
        return 'SEARCH';
      }
    }

    // Direct visit (no referrer and no UTM)
    if (!referrer && !utmSource) {
      return 'DIRECT';
    }

    return 'OTHER';
  }

  // Clean and categorize referrer
  cleanReferrer(referrer) {
    if (!referrer) return 'Direct';
    
    try {
      const url = new URL(referrer);
      const hostname = url.hostname.toLowerCase();
      
      // Social media platforms
      if (hostname.includes('facebook.com')) return 'Facebook';
      if (hostname.includes('twitter.com') || hostname.includes('x.com')) return 'Twitter/X';
      if (hostname.includes('instagram.com')) return 'Instagram';
      if (hostname.includes('linkedin.com')) return 'LinkedIn';
      if (hostname.includes('youtube.com')) return 'YouTube';
      if (hostname.includes('tiktok.com')) return 'TikTok';
      if (hostname.includes('pinterest.com')) return 'Pinterest';
      if (hostname.includes('snapchat.com')) return 'Snapchat';
      
      // Search engines
      if (hostname.includes('google.com')) return 'Google';
      if (hostname.includes('bing.com')) return 'Bing';
      if (hostname.includes('yahoo.com')) return 'Yahoo';
      if (hostname.includes('duckduckgo.com')) return 'DuckDuckGo';
      
      // Email providers
      if (hostname.includes('gmail.com')) return 'Gmail';
      if (hostname.includes('outlook.com') || hostname.includes('hotmail.com')) return 'Outlook';
      if (hostname.includes('yahoo.com')) return 'Yahoo Mail';
      
      // Return the domain name
      return hostname.replace('www.', '');
    } catch (error) {
      return 'Direct';
    }
  }

  // Get country using IP geolocation
  async getCountry() {
    try {
      // Try to get country from IP geolocation service
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.country_name || data.country_code || 'Unknown';
      }
    } catch (error) {
      console.log('Geolocation service unavailable, using fallback');
    }

    // Fallback: Try to detect from timezone
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const countryMap = {
        'America/New_York': 'United States',
        'America/Chicago': 'United States',
        'America/Denver': 'United States',
        'America/Los_Angeles': 'United States',
        'Europe/London': 'United Kingdom',
        'Europe/Paris': 'France',
        'Europe/Berlin': 'Germany',
        'Europe/Madrid': 'Spain',
        'Europe/Rome': 'Italy',
        'Asia/Tokyo': 'Japan',
        'Asia/Shanghai': 'China',
        'Asia/Kolkata': 'India',
        'Australia/Sydney': 'Australia',
        'Africa/Lagos': 'Nigeria',
        'Africa/Cairo': 'Egypt',
        'America/Sao_Paulo': 'Brazil',
        'America/Mexico_City': 'Mexico',
        'America/Toronto': 'Canada'
      };
      
      return countryMap[timezone] || 'Unknown';
    } catch (error) {
      return 'Unknown';
    }
  }

  // Get IP address (simplified - in production, use a service)
  async getIPAddress() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Could not fetch IP address:', error);
      return 'unknown';
    }
  }

  // Track a visit
  async trackVisit(merchantId, storeId, entryPage = '/') {
    if (this.isTracking) return;

    this.isTracking = true;
    this.merchantId = merchantId;
    this.storeId = storeId;

    try {
      const deviceInfo = this.getDeviceInfo();
      const utmParams = this.getUTMParameters();
      const qrParams = this.getQRParameters();
      const sourceType = this.getSourceType();
      const ipAddress = await this.getIPAddress();

      const visitData = {
        merchantId,
        storeId,
        visitorId: this.visitorId,
        sessionId: this.sessionId,
        ipAddress,
        userAgent: navigator.userAgent,
        referrer: this.cleanReferrer(document.referrer),
        country: await this.getCountry(),
        city: '', // Could be enhanced with geolocation
        device: deviceInfo.device,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        entryPage,
        // UTM parameters
        ...utmParams,
        // Source tracking (QR codes, campaigns, etc.)
        sourceType,
        sourceId: qrParams.qrId || utmParams.utmContent || null,
        qrCodeType: qrParams.qrCodeType,
        productId: qrParams.productId
      };

      // Log QR scan for debugging
      if (qrParams.isQRScan) {
        console.log('[VisitTracker] QR code scan detected:', {
          qrCodeType: qrParams.qrCodeType,
          qrId: qrParams.qrId,
          productId: qrParams.productId
        });
      }

      // Import the trackVisit action dynamically to avoid circular imports
      // const { trackVisit } = await import('../features/visits/visitSlice');

      // Dispatch the action (this would need to be called from a component with dispatch)
      return visitData;
    } catch (error) {
      console.error('Error tracking visit:', error);
      this.isTracking = false;
      throw error;
    }
  }

  // Update visit duration
  updateDuration() {
    if (!this.isTracking) return;

    const duration = Math.floor((Date.now() - this.startTime) / 1000);
    
    // Update duration in localStorage for persistence
    localStorage.setItem('revstar_visit_duration', duration.toString());
    
    return duration;
  }

  // Mark conversion
  markConversion(conversionType, conversionValue = 0) {
    if (!this.isTracking) return;

    const conversionData = {
      sessionId: this.sessionId,
      conversionType,
      conversionValue
    };

    return conversionData;
  }

  // Get current visit data
  getCurrentVisitData() {
    return {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      startTime: this.startTime,
      duration: this.updateDuration(),
      isTracking: this.isTracking,
      merchantId: this.merchantId,
      storeId: this.storeId
    };
  }

  // Reset session (for new page loads)
  resetSession() {
    this.sessionId = this.getOrCreateSessionId();
    this.startTime = Date.now();
    this.isTracking = false;
  }

  // Clean up on page unload
  cleanup() {
    if (this.isTracking) {
      this.updateDuration();
    }
  }
}

// Create singleton instance
const visitTracker = new VisitTracker();

// Set up page unload cleanup
window.addEventListener('beforeunload', () => {
  visitTracker.cleanup();
});

// Set up visibility change cleanup (when user switches tabs)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    visitTracker.cleanup();
  }
});

export default visitTracker;
